import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DailySettlementStatuses } from '../payment.data';
import {
  CombinedPayoutItem,
  DailySettlementItem,
  PayoutPart,
  PaymentsDashboardService,
  TrackerPayoutItem,
} from '../../services/payments-dashboard.service';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../shared/components/pagination-bar/pagination-bar.component';
import { PaymentsToolbarFiltersComponent } from '../components/payments-toolbar-filters/payments-toolbar-filters.component';
import { EmptyResultComponent } from '../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { ProfileService } from '../../services/profile.service';
import { NotificationService } from '../../services/notification.service';
import { LocationService } from '../../services/location.service';
import {
  buildPaymentsLocationApiFilters,
  mapLocationsForPaymentsFilter,
  PAYMENTS_LOCATION_STORAGE_KEY,
  PaymentsLocationOption,
  resolvePaymentsLocationLabel,
} from '../payments-location-filter.util';
import { downloadBlob } from '../download-blob.util';

export interface PayoutTrackerRow {
  id: string;
  businessDate: string;
  locationName: string;
  /** Null on a payout row: a payout covers several days. */
  netCollected: number | null;
  refund: number;
  totalPayout: number;
  bankName: string | null;
  bankLast4: string | null;
  status: string;
  statusLabel: string;
  statusClass: string;
  arriveBy: string;
  /** CWS-1615: on a day paid by more than one payout, one line per payout ("Stripe paid $14.40 · Oct 8, 2026"). */
  payoutLines: string[];
  /** Money moved between days: "−$60.00 taken from Oct 6", "includes −$60.00 from Oct 5". */
  movedMoneyLines: string[];
  /** CWS-1615: why an Upcoming day shows no date, on hover. */
  statusHint: string | null;
}

/** CWS-729: full-width "Switched to manual payouts" row between a location's before- and after-switch days. */
export interface PayoutTrackerSwitchDivider {
  kind: 'switch-divider';
  id: string;
  label: string;
}

export type PayoutTrackerTableRow = PayoutTrackerRow | PayoutTrackerSwitchDivider;

const isStripePayout = (item: CombinedPayoutItem): item is TrackerPayoutItem =>
  item.kind === 'payout';

const PAYOUT_TRACKER_STATUS_STORAGE_KEY = 'paymentsPayoutTrackerStatusFilter';

/**
 * Payout Tracker tab: the days our payout cron pays from the switch to manual payouts
 * on, then, as the Payouts list shows them, every payout no such day lists and Stripe's
 * upcoming balance from before the switch — money covering several days, so no business
 * day.
 */
@Component({
  selector: 'app-payout-tracker',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatTooltipModule,
    PaymentsToolbarFiltersComponent,
    PaginationBarComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
  ],
  templateUrl: './payout-tracker.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  styleUrl: './payout-tracker.component.scss',
})
export class PayoutTrackerComponent implements OnInit, OnDestroy {
  selectedStatuses: string[] = [];
  rows: PayoutTrackerRow[] = [];
  tableLoading = true;
  exportLoading = false;

  dataSource = new MatTableDataSource<PayoutTrackerTableRow>();
  /** mat-table `when` predicate for the switch divider row. */
  readonly isSwitchDivider = (_index: number, row: PayoutTrackerTableRow): boolean =>
    'kind' in row && row.kind === 'switch-divider';
  displayedColumns: string[] = [
    'businessDate',
    'location',
    'netCollected',
    'refund',
    'totalPayout',
    'bank',
    'statusArrival',
  ];
  shimmerColumns: ShimmerColumn[] = [
    { label: 'Business Day', width: 'sm' },
    { label: 'Location', width: 'md' },
    { label: 'Net Collected', width: 'sm' },
    { label: 'Refund', width: 'sm' },
    { label: 'Total Payout', width: 'sm' },
    { label: 'Bank', width: 'md' },
    { label: 'Status', width: 'md' },
  ];

  settlementStatuses = DailySettlementStatuses;
  locationOptions: PaymentsLocationOption[] = [];
  selectedLocationId = '';
  selectedLocationLabel = 'All Locations';
  totalRecords = 0;
  currentPage = 1;
  itemsPerPage = 10;
  paginationData: PaginationData = {
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10,
  };

  private readonly destroy$ = new Subject<void>();
  private requestToken = 0;

  private readonly statusLabels: Record<string, string> = {
    pending: 'Upcoming',
    paid: 'Completed',
    partial: 'Partly paid',
    // A payout row only: a payout that never reached the bank.
    failed: 'Failed',
    canceled: 'Canceled',
  };

  constructor(
    private readonly paymentsService: PaymentsDashboardService,
    private readonly profileService: ProfileService,
    private readonly locationService: LocationService,
    private readonly messageService: NotificationService
  ) {
    this.restoreFilters();
  }

  ngOnInit(): void {
    this.profileService.locations$.pipe(takeUntil(this.destroy$)).subscribe(locations => {
      this.locationOptions = mapLocationsForPaymentsFilter(locations);
      this.syncSelectedLocation();
    });
    this.profileService.loadLocations();
    this.loadSettlements();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onStatusChange(): void {
    this.currentPage = 1;
    localStorage.setItem(PAYOUT_TRACKER_STATUS_STORAGE_KEY, JSON.stringify(this.selectedStatuses));
    this.loadSettlements();
  }

  onLocationChange(locationId: string): void {
    this.selectedLocationId = locationId;
    this.selectedLocationLabel = resolvePaymentsLocationLabel(locationId, this.locationOptions);
    this.currentPage = 1;
    localStorage.setItem(PAYMENTS_LOCATION_STORAGE_KEY, locationId);
    this.syncShopLocation(locationId);
    this.loadSettlements();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadSettlements();
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
    this.currentPage = 1;
    this.loadSettlements();
  }

  /** Downloads every row the current filters list, not only this page. */
  async onExport(): Promise<void> {
    if (this.exportLoading) return;
    this.exportLoading = true;
    try {
      // No timezone: the export renders business_date as-is and the arrival date in
      // UTC (matching the table and the Stripe dashboard) — nothing is localized.
      const blob = await firstValueFrom(
        this.paymentsService.exportDailySettlements({ filters: this.buildFilters() })
      );
      downloadBlob(blob, `payout-tracker-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Export failed',
        detail: 'Unable to export settlements. Please try again.',
      });
    } finally {
      this.exportLoading = false;
    }
  }

  loadSettlements(): void {
    const token = ++this.requestToken;
    this.tableLoading = true;
    this.clearTable();

    const filters = this.buildFilters();
    const body = {
      paging: { page: this.currentPage, limit: this.itemsPerPage },
      ...(filters ? { filters } : {}),
    };
    this.paymentsService
      .listCombinedPayouts(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          if (token !== this.requestToken) return;
          const items = data?.items ?? [];
          this.rows = items.map(item =>
            isStripePayout(item) ? this.mapPayoutToRow(item) : this.mapItemToRow(item)
          );
          // A full-width divider only reads right for a single location; across
          // locations it would split other locations' days that never switched.
          this.dataSource.data = filters?.locationId
            ? this.withSwitchDividers(items, this.rows)
            : this.rows;
          this.totalRecords = data?.pagination?.total ?? 0;
          this.paginationData = {
            currentPage: data?.pagination?.page ?? this.currentPage,
            totalPages: data?.pagination?.totalPages ?? 0,
            totalItems: this.totalRecords,
            itemsPerPage: data?.pagination?.limit ?? this.itemsPerPage,
          };
          this.tableLoading = false;
        },
        error: () => {
          if (token !== this.requestToken) return;
          this.clearTable();
          this.tableLoading = false;
        },
      });
  }

  private clearTable(): void {
    this.rows = [];
    this.dataSource.data = [];
    this.totalRecords = 0;
    this.paginationData = {
      currentPage: this.currentPage,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: this.itemsPerPage,
    };
  }

  private restoreFilters(): void {
    try {
      const saved = localStorage.getItem(PAYOUT_TRACKER_STATUS_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) this.selectedStatuses = parsed;
      }
      const savedLocation = localStorage.getItem(PAYMENTS_LOCATION_STORAGE_KEY);
      if (savedLocation !== null) this.selectedLocationId = savedLocation;
    } catch {
      this.selectedStatuses = [];
      this.selectedLocationId = '';
    }
    this.selectedLocationLabel = resolvePaymentsLocationLabel(
      this.selectedLocationId,
      this.locationOptions
    );
  }

  private syncSelectedLocation(): void {
    const isValid =
      !this.selectedLocationId ||
      this.locationOptions.some(o => o.value === this.selectedLocationId);
    if (!isValid) {
      this.selectedLocationId = '';
      localStorage.setItem(PAYMENTS_LOCATION_STORAGE_KEY, '');
    }
    this.selectedLocationLabel = resolvePaymentsLocationLabel(
      this.selectedLocationId,
      this.locationOptions
    );
    this.syncShopLocation(this.selectedLocationId);
  }

  private syncShopLocation(locationId: string): void {
    if (!locationId) {
      this.locationService.clearShopLocation();
      return;
    }
    this.locationService.setShopLocationById(locationId);
  }

  private buildFilters(): { locationId?: string; status?: string[] } | undefined {
    const filters: { locationId?: string; status?: string[] } = {
      ...buildPaymentsLocationApiFilters(this.selectedLocationId, this.locationOptions),
    };
    if (this.selectedStatuses.length) filters.status = this.selectedStatuses;
    return Object.keys(filters).length ? filters : undefined;
  }

  private mapItemToRow(item: DailySettlementItem): PayoutTrackerRow {
    // Nothing to pay: a $0 day never produces a payout (the cron skips it), so
    // it is settled by definition — Completed, with no bank or arrival details.
    const zeroNet = item.netAmount <= 0;
    const {
      status,
      arriveBy,
      payoutLines = [],
    } = zeroNet ? { status: 'paid', arriveBy: '—' } : this.resolvePayoutState(item);
    // Refunds beyond the day's money: the day's own result is negative. Nothing is sent
    // for it — the shortfall comes off the next day's payout (movedMoneyLines says so).
    const refundShortfall = zeroNet ? (item.refundCarryoverOut ?? 0) : 0;
    let totalPayout = item.totalPayoutAmount ?? item.netAmount;
    if (zeroNet) totalPayout = refundShortfall > 0 ? -refundShortfall : item.netAmount;
    return {
      id: item.id,
      businessDate: this.formatBusinessDate(item.businessDate),
      locationName: item.locationName?.trim() || '—',
      netCollected: item.grossAmount - item.feeAmount,
      refund: item.refundAmount,
      totalPayout,
      bankName: zeroNet ? null : item.bankName?.trim() || null,
      bankLast4: zeroNet ? null : item.bankLast4?.trim() || null,
      status,
      statusLabel: this.statusLabels[status] ?? status,
      statusClass: status,
      arriveBy,
      payoutLines,
      movedMoneyLines: this.describeMovedMoney(item),
      statusHint: status === 'pending' && !item.stripePayoutId ? this.upcomingHint(item) : null,
    };
  }

  /**
   * Why a day's Total Payout differs from its own Net Collected − Refund: refunds beyond
   * a day's money are taken from the next day's payout, and payments recorded after a
   * day was paid go out with a later one.
   */
  private describeMovedMoney(item: DailySettlementItem): string[] {
    const lines: string[] = [];
    const refundIn = item.refundCarryoverIn ?? 0;
    const refundOut = item.refundCarryoverOut ?? 0;
    const lateIn = item.latePaymentCarryIn ?? 0;
    const lateOut = item.latePaymentCarryOut ?? 0;
    if (refundIn > 0) {
      const from = item.refundCarryoverFrom
        ? ` from ${this.formatShortDay(item.refundCarryoverFrom)}`
        : '';
      lines.push(`includes −${this.formatAmount(refundIn)}${from}`);
    }
    if (lateIn > 0) lines.push(`includes ${this.formatAmount(lateIn)} from earlier days`);
    if (refundOut > 0) {
      const to = item.refundCarryoverTo
        ? this.formatShortDay(item.refundCarryoverTo)
        : 'the next payout';
      lines.push(`−${this.formatAmount(refundOut)} taken from ${to}`);
    }
    if (lateOut > 0) lines.push(`${this.formatAmount(lateOut)} moved to a later payout`);
    return lines;
  }

  /** A calendar day (YYYY-MM-DD) as "Oct 6", from its parts so it never shifts with the viewer's timezone. */
  private formatShortDay(day: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
    if (!match) return day;
    const [, year, month, date] = match.map(Number);
    return new Date(Date.UTC(year, month - 1, date)).toLocaleString('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
    });
  }

  /**
   * A payout no day lists — almost always one of Stripe's own. It covers several days,
   * so it has no business day and no Net Collected or Refund; its amount is the payout.
   */
  private mapPayoutToRow(item: TrackerPayoutItem): PayoutTrackerRow {
    const { status, arriveBy, statusHint } = this.resolveStripePayoutState(item);
    return {
      id: item.id,
      businessDate: '—',
      locationName: item.locationName?.trim() || '—',
      netCollected: null,
      refund: 0,
      totalPayout: item.amount,
      bankName: item.bankName?.trim() || null,
      bankLast4: item.bankLast4?.trim() || null,
      status,
      statusLabel: this.statusLabels[status] ?? status,
      statusClass: status,
      arriveBy,
      payoutLines: [],
      movedMoneyLines: [],
      statusHint,
    };
  }

  /**
   * The same words as a day: Completed once the payout reached the bank ("Paid by
   * Stripe · <arrival>" for Stripe's automatic payouts, as the tracker said), Upcoming
   * — with no date — while it is on its way or still Stripe's upcoming balance. A
   * failed or canceled payout says so, with Stripe's reason on hover.
   */
  private resolveStripePayoutState(item: TrackerPayoutItem): {
    status: string;
    arriveBy: string;
    statusHint: string | null;
  } {
    if (item.status === 'completed') {
      const arrival = this.formatArriveBy(item.arrivalDate);
      const arriveBy = item.automatic ? `Paid by Stripe · ${arrival}` : arrival;
      return { status: 'paid', arriveBy, statusHint: null };
    }
    if (item.status === 'failed' || item.status === 'canceled') {
      const reason = item.failureMessage || item.failureCode || null;
      return { status: item.status, arriveBy: '—', statusHint: reason };
    }
    if (item.resourceType === 'balance_available') {
      return {
        status: 'pending',
        arriveBy: 'Available in Stripe · waiting for payout',
        statusHint:
          'Money from before the switch to manual payouts, already available in Stripe. ' +
          'Our payouts pay it out day by day, oldest first.',
      };
    }
    return { status: 'pending', arriveBy: '—', statusHint: null };
  }

  /**
   * CWS-729: a day reads Completed only once the Stripe payout covering it reached
   * the bank — Stripe's automatic payout for a 'stripe_auto' day, our cron's for a
   * manual day. Until then it is Upcoming, with no date (before any payout covers it,
   * the hover says who creates it). Real Stripe data only — no estimates.
   *
   * CWS-1615: a day whose money leaves Stripe in more than one payout (split across two
   * automatic payouts, or a pre-switch day Stripe paid part of before our cron took the
   * rest over) lists every payout instead, and reads Partly paid until all have landed.
   */
  private resolvePayoutState(item: DailySettlementItem): {
    status: string;
    arriveBy: string;
    payoutLines?: string[];
  } {
    const parts = item.payoutParts ?? [];
    if (parts.length > 1) {
      return this.resolveSplitPayoutState(parts, item.locationTimezone ?? null);
    }

    // A failed/canceled payout never reaches the bank; Stripe retries it as a new payout.
    const hasLivePayout =
      Boolean(item.stripePayoutId) && !['failed', 'canceled'].includes(item.payoutStatus ?? '');
    const credited = hasLivePayout && item.payoutStatus === 'paid';

    if (item.payoutMode === 'stripe_auto') {
      if (!credited) return { status: 'pending', arriveBy: '—' };
      return {
        status: 'paid',
        arriveBy:
          item.paidAt || item.arrivalDate
            ? `Paid by Stripe · ${this.formatPaidDay(item.paidAt, item.arrivalDate, item.locationTimezone ?? null)}`
            : 'Paid by Stripe',
      };
    }
    // Manual day: 'paid' only means our payout was created — the bank is credited
    // once the payout itself reaches 'paid'. 'processing' is the cron's claim on the
    // row for the length of one Stripe call: still Upcoming to the user (CWS-1615).
    if (
      item.status === 'pending' ||
      item.status === 'processing' ||
      (item.status === 'paid' && !credited)
    ) {
      return { status: 'pending', arriveBy: '—' };
    }
    return {
      status: item.status,
      arriveBy: this.formatPaidDay(item.paidAt, item.arrivalDate, item.locationTimezone ?? null),
    };
  }

  /**
   * CWS-1615: one line per payout. Completed once every part reached the bank, Partly
   * paid while some did and some are still on their way, Upcoming while none has.
   */
  private resolveSplitPayoutState(
    parts: PayoutPart[],
    timezone: string | null
  ): {
    status: string;
    arriveBy: string;
    payoutLines: string[];
  } {
    const paid = parts.filter(part => part.paid).length;
    let status = 'partial';
    if (paid === parts.length) status = 'paid';
    else if (paid === 0) status = 'pending';
    return {
      status,
      arriveBy: '—',
      payoutLines: parts.map(part => this.describePayoutPart(part, timezone)),
    };
  }

  /**
   * "Stripe paid $14.40 · Oct 8, 2026", "Paid $57.60 · Oct 10, 2026" (ours),
   * "$57.60 pending" (payout in transit — Upcoming shows no date),
   * "$57.60 pending · Payout failed · Stripe will retry" or
   * "Pending balance $57.60" (still in the Stripe balance, no payout holds it yet).
   */
  private describePayoutPart(part: PayoutPart, timezone: string | null): string {
    const amount = this.formatAmount(part.amount);
    if (part.paid) {
      const who = part.source === 'stripe' ? 'Stripe paid' : 'Paid';
      return part.paidAt || part.arrivalDate
        ? `${who} ${amount} · ${this.formatPaidDay(part.paidAt ?? null, part.arrivalDate, timezone)}`
        : `${who} ${amount}`;
    }
    if (part.failed) return `${amount} pending · Payout failed · Stripe will retry`;
    if (part.arrivalDate) return `${amount} pending`;
    return `Pending balance ${amount}`;
  }

  private formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  }

  /**
   * CWS-1615: an Upcoming day with no payout yet shows no date — on hover, who creates
   * the payout: our daily payout on a manual day, Stripe on an automatic one.
   */
  private upcomingHint(item: DailySettlementItem): string {
    return item.payoutMode === 'stripe_auto'
      ? 'Stripe pays this out automatically.'
      : 'Payout is created once the funds are available in Stripe.';
  }

  /**
   * CWS-1615: the day a payout was paid — when Stripe reported it (an instant, shown
   * as the calendar day in the location's timezone), else its arrival date.
   */
  private formatPaidDay(
    paidAt: string | null | undefined,
    arrivalDate: string | null,
    timezone: string | null
  ): string {
    if (!paidAt) return this.formatArriveBy(arrivalDate);
    const date = new Date(paidAt);
    if (Number.isNaN(date.getTime())) return this.formatArriveBy(arrivalDate);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    try {
      return date.toLocaleString('en-US', { ...options, timeZone: timezone ?? undefined });
    } catch {
      return date.toLocaleString('en-US', options);
    }
  }

  /**
   * Stripe's arrival_date is a calendar day anchored at midnight UTC — not an
   * instant. Format its UTC date parts, date only, so the shown day matches the
   * Stripe dashboard exactly and never shifts with the viewer's timezone
   * (rendering that midnight in a local zone shows 5:30 AM in IST or even the
   * previous evening in US zones).
   */
  private formatArriveBy(arrivalDate: string | null): string {
    if (!arrivalDate) return '—';
    const date = new Date(arrivalDate);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /**
   * CWS-729: insert a "Switched to manual payouts" divider above the row the API flags —
   * the selected location's first payout dated before its switch day.
   */
  private withSwitchDividers(
    items: CombinedPayoutItem[],
    rows: PayoutTrackerRow[]
  ): PayoutTrackerTableRow[] {
    const tableRows: PayoutTrackerTableRow[] = [];
    items.forEach((item, index) => {
      if (item.isSwitchBoundary && item.switchDate) {
        const when = this.formatSwitchMoment(
          item.switchDate,
          item.locationTimezone ?? null,
          isStripePayout(item) ? (item.switchedAt ?? null) : null
        );
        tableRows.push({
          kind: 'switch-divider',
          id: `switch-${item.id}`,
          label: `Switched to manual payouts · ${when}`,
        });
      }
      tableRows.push(rows[index]);
    });
    return tableRows;
  }

  /**
   * When the location switched, in its timezone: the real moment when the API sends it
   * ("Oct 14, 2026, 3:00 PM PT"), else 12:00 AM on the switch day ("Sep 30, 2026,
   * 12:00 AM PT") — a calendar date, formatted from its parts so it never shifts with
   * the viewer's timezone.
   */
  private formatSwitchMoment(
    switchDate: string,
    timezone: string | null,
    switchedAt: string | null = null
  ): string {
    const at = switchedAt ? new Date(switchedAt) : null;
    if (at && !Number.isNaN(at.getTime())) {
      try {
        return new Intl.DateTimeFormat('en-US', {
          timeZone: timezone || 'UTC',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          // "PT" for a location; with none, "UTC" (shortGeneric would say "GMT+0").
          timeZoneName: timezone ? 'shortGeneric' : 'short',
        }).format(at);
      } catch {
        // unknown timezone: fall back to the switch day
      }
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(switchDate);
    if (!match) return switchDate;
    const [, year, month, day] = match.map(Number);
    const dayLabel = new Date(Date.UTC(year, month - 1, day)).toLocaleString('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    let zoneLabel = '';
    if (timezone) {
      try {
        zoneLabel =
          new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'shortGeneric' })
            .formatToParts(new Date(Date.UTC(year, month - 1, day, 12)))
            .find(part => part.type === 'timeZoneName')?.value ?? '';
      } catch {
        zoneLabel = ''; // unknown timezone: show the time without a zone
      }
    }
    return `${dayLabel}, 12:00 AM${zoneLabel ? ` ${zoneLabel}` : ''}`;
  }

  /**
   * business_date is a calendar day (YYYY-MM-DD), not an instant — build the Date
   * from its parts so the shown day never shifts with the viewer's timezone.
   */
  private formatBusinessDate(businessDate: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(businessDate ?? '');
    if (!match) return businessDate || '—';
    const [, year, month, day] = match;
    // Figma spec: "Wed, Aug 19"
    return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }
}
